import { useState } from 'react'
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { parseIsoZonedDateTime, formatZonedDateTime } from '@portal/pkg-format/date'
import { useHasEnablement } from '@portal/pkg-authorization'
import { OverflowCell } from '@portal/pkg-ui'
import type { UserListItemResponse } from '@portal/pkg-api/gen/admin-console'
import { UserStatus } from '@portal/pkg-api/gen/admin-console'
import { UserQueries } from '../queries'
import { useUpdateUserStatus, useImpersonateUser } from '../mutations'
import { isLicenseLimitExceeded, isOrganizationBusy } from '../problems'

function StatusChip({ status }: { status: UserStatus }) {
  const { t } = useTranslation('admin-console')
  const isEnabled = status === UserStatus.ENABLED
  return (
    <Chip
      label={t(isEnabled ? 'users.table.status.enabled' : 'users.table.status.disabled')}
      color={isEnabled ? 'success' : 'default'}
      size="small"
      variant="outlined"
    />
  )
}

function LastLogin({ value }: { value?: string }) {
  const { i18n } = useTranslation()
  if (!value) return <>{'—'}</>
  return <>{formatZonedDateTime(parseIsoZonedDateTime(value), i18n.language)}</>
}

type ConfirmAction = 'status' | 'impersonate' | null

function UserActionMenu({ user, orgId }: { user: UserListItemResponse; orgId?: number }) {
  const { t } = useTranslation('admin-console')
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const updateStatus = useUpdateUserStatus(orgId)
  const impersonate = useImpersonateUser()
  const { data: currentUserId } = useQuery(UserQueries.currentUserId())
  const isCurrentUser = user.id === currentUserId
  const isEnabled = user.status === UserStatus.ENABLED
  const canImpersonate =
    useHasEnablement('UserImpersonation', 'MANAGE', { global: true }) && !isCurrentUser && isEnabled
  const canEdit = useHasEnablement('User', 'ADMIN', { global: true })
  const canActivateDeactivate = !user.primaryContact && !isCurrentUser
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null)
  const targetStatus = isEnabled ? UserStatus.DISABLED : UserStatus.ENABLED
  const actionKey = isEnabled ? 'deactivate' : 'activate'
  const displayName = t('displayName', {
    givenName: user.givenName,
    familyName: user.familyName,
  })

  const openConfirm = (action: ConfirmAction) => {
    setAnchorEl(null)
    setConfirmAction(action)
  }

  const handleConfirm = () => {
    if (confirmAction === 'status') {
      const messageKey = isEnabled ? 'users.table.userDeactivated' : 'users.table.userActivated'
      updateStatus.mutate(
        { id: user.id, status: targetStatus },
        {
          onSuccess: () => enqueueSnackbar(t(messageKey), { variant: 'success' }),
          onError: (error: unknown) => {
            const errorKey = isLicenseLimitExceeded(error)
              ? 'users.table.licenseLimitExceeded'
              : isOrganizationBusy(error)
                ? 'users.table.organizationBusy'
                : 'users.table.statusUpdateFailed'
            enqueueSnackbar(t(errorKey), { variant: 'error' })
          },
        },
      )
    } else if (confirmAction === 'impersonate') {
      impersonate.mutate({ userId: user.id })
    }
    setConfirmAction(null)
  }

  const confirmMessage =
    confirmAction === 'impersonate'
      ? t('users.table.actions.loginAsConfirm', { displayName })
      : t(`users.table.actions.${actionKey}Confirm`, { displayName })

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {canEdit && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null)
              void navigate({
                to: '/preferences/users/$userId',
                params: { userId: String(user.id) },
              })
            }}
          >
            {t('users.table.actions.edit')}
          </MenuItem>
        )}
        {canActivateDeactivate && (
          <MenuItem onClick={() => openConfirm('status')}>
            {t(`users.table.actions.${actionKey}`)}
          </MenuItem>
        )}
        {canImpersonate && (
          <MenuItem onClick={() => openConfirm('impersonate')}>
            {t('users.table.actions.loginAs')}
          </MenuItem>
        )}
      </Menu>
      <Dialog open={confirmAction !== null} onClose={() => setConfirmAction(null)}>
        <DialogContent>
          <DialogContentText>{confirmMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>{t('cancel')}</Button>
          <Button variant="contained" onClick={handleConfirm}>
            {t('users.table.actions.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

function UserRow({ user, orgId }: { user: UserListItemResponse; orgId?: number }) {
  const { t } = useTranslation('admin-console')
  return (
    <TableRow>
      <OverflowCell>
        {t('displayName', { givenName: user.givenName, familyName: user.familyName })}
      </OverflowCell>
      <OverflowCell>{user.email.toLowerCase()}</OverflowCell>
      <TableCell>
        <StatusChip status={user.status} />
      </TableCell>
      <TableCell>{user.licenseType}</TableCell>
      <TableCell>
        <LastLogin value={user.lastLogin} />
      </TableCell>
      <TableCell>
        {user.authSource
          ? t(`users.table.authSource.${user.authSource}`, { defaultValue: user.authSource })
          : '—'}
      </TableCell>
      <TableCell align="right">
        <UserActionMenu user={user} orgId={orgId} />
      </TableCell>
    </TableRow>
  )
}

function filterUsers(users: UserListItemResponse[], search: string) {
  const term = search.toLowerCase().trim()
  if (!term) return users
  return users.filter((user) => {
    const fullName = `${user.givenName} ${user.familyName}`.toLowerCase()
    return fullName.includes(term) || user.email.toLowerCase().includes(term)
  })
}

export function UsersTable({ search, orgId }: { search: string; orgId?: number }) {
  const { t } = useTranslation('admin-console')
  const { data: users } = useSuspenseQuery(UserQueries.list(orgId))
  const filtered = filterUsers(users, search)

  return (
    <TableContainer component={Paper} elevation={0} sx={{ width: '100%' }}>
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.50' }}>
            <TableCell>{t('users.table.columns.name')}</TableCell>
            <TableCell>{t('users.table.columns.email')}</TableCell>
            <TableCell>{t('users.table.columns.status')}</TableCell>
            <TableCell>{t('users.table.columns.licenseType')}</TableCell>
            <TableCell>{t('users.table.columns.lastLogin')}</TableCell>
            <TableCell>{t('users.table.columns.authSource')}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                {t('users.table.noResults')}
              </TableCell>
            </TableRow>
          ) : (
            filtered.map((user) => <UserRow key={user.id} user={user} orgId={orgId} />)
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
